import { useEffect } from "react";
import type { PageNode } from "@baze/schema";
import { DONATION_SCRIPT, INVITATION_SCRIPT, LESSONS_SCRIPT, MOTION_SCRIPT, SLIDER_SCRIPT, needsDonationScript, needsInvitationScript, needsLessonsScript, needsMotionScript, needsResponsesScript, needsSliderScript, needsStoreScript, responsesScript, storeScript } from "@baze/engine/runtime";

/**
 * Runs the live site's inline enhancers (sliders, lessons search, donation form, section motion, countdown and invitation opening, wishes/RSVP) on a
 * draft preview, so it behaves like the published page. Forms run in demo mode: nothing is sent. No
 * analytics. React never executes an inline <script> it renders, so each one is created here; the
 * scripts are safe to run again, and `runKey` (page, draft version) re-runs them on new DOM.
 */
export function SiteRuntime({ page, runKey }: { page: Pick<PageNode, "sections">; runKey: string }) {
  const slider = needsSliderScript(page);
  const invitation = needsInvitationScript(page);
  const responses = needsResponsesScript(page);
  const lessons = needsLessonsScript(page);
  const donation = needsDonationScript(page);
  const motion = needsMotionScript(page);
  const store = needsStoreScript(page);
  useEffect(() => {
    const sources = [slider && SLIDER_SCRIPT, invitation && INVITATION_SCRIPT, responses && responsesScript("", true, true), lessons && LESSONS_SCRIPT, donation && DONATION_SCRIPT, motion && MOTION_SCRIPT, store && storeScript("")].filter((s): s is string => !!s);
    const els = sources.map((text) => {
      const s = document.createElement("script");
      s.text = text;
      document.body.appendChild(s);
      return s;
    });
    return () => els.forEach((s) => s.remove());
  }, [runKey, slider, invitation, responses, lessons, donation, motion, store]);
  return null;
}
